/**
 * The attention column: the one list both Overview views show, 400 px
 * wide on the right (twin slice 1, plan W5 step 13; ideation §6).
 *
 * In order: the site rows; Needs you (up to three cards, then "+N more");
 * Nexsoss (please-call first, as the selector orders it); "Picked up (n)",
 * collapsed; Info; then "Right now". Groups are by who acts; inside a
 * group the selector has already ordered by band, stamp and status, and
 * each card carries its band as a glyph. Nothing reorders here.
 *
 * Every word comes from the selector, the copy tables or the structural
 * labels in `card-words.ts`. OverviewFrame mounts it once, hosting both
 * views (plan W5 step 14), behind the two flags.
 */
import { useState } from 'react';
import { ATTENTION_LABELS, NEEDS_YOU_SHOWN } from '../../../state/attention/card-words';
import type { RightNowGroup } from '../../../state/attention/right-now';
import type { AttentionItem, AttentionModel } from '../../../state/attention/select';
import type { SiteTimeZone } from '../../../state/attention/words';
import { AttentionCard, type AttentionCardActions } from './AttentionCard';
import { RightNowList } from './RightNowList';
import './attention.css';

export interface AttentionColumnProps extends AttentionCardActions {
  model: AttentionModel;
  clock: SiteTimeZone;
  /** `rightNow()`'s rows; omitted, no "Right now" list. */
  rightNow?: readonly RightNowGroup[];
}

function Cards({
  items,
  ...rest
}: { items: readonly AttentionItem[]; clock: SiteTimeZone } & AttentionCardActions) {
  return (
    <ul className="nx-attention-column__cards">
      {items.map((it) => (
        <li key={it.key}>
          <AttentionCard item={it} {...rest} />
        </li>
      ))}
    </ul>
  );
}

export function AttentionColumn({
  model,
  clock,
  rightNow,
  onShowWhere,
  onOpenPanel,
}: AttentionColumnProps) {
  const [allNeedsYou, setAllNeedsYou] = useState(false);
  const cardProps = { clock, onShowWhere, onOpenPanel };
  const hidden = Math.max(0, model.needsYou.length - NEEDS_YOU_SHOWN);
  const needsYou = allNeedsYou ? model.needsYou : model.needsYou.slice(0, NEEDS_YOU_SHOWN);
  return (
    <aside className="nx-attention-column" aria-label={ATTENTION_LABELS.column}>
      {model.siteRows.length > 0 ? (
        <section className="nx-attention-column__group" data-group="site">
          <Cards items={model.siteRows} {...cardProps} />
        </section>
      ) : null}

      {model.needsYou.length > 0 ? (
        <section className="nx-attention-column__group" data-group="needs_you">
          <h3 className="nx-attention-column__heading">{ATTENTION_LABELS.needsYou}</h3>
          <Cards items={needsYou} {...cardProps} />
          {hidden > 0 ? (
            <button
              type="button"
              className="nx-attention-column__more"
              aria-expanded={allNeedsYou}
              onClick={() => setAllNeedsYou((v) => !v)}
            >
              {allNeedsYou ? ATTENTION_LABELS.fewer : ATTENTION_LABELS.more(hidden)}
            </button>
          ) : null}
        </section>
      ) : null}

      {model.nexsoss.length > 0 || model.pickedUp.length > 0 ? (
        <section className="nx-attention-column__group" data-group="nexsoss">
          <h3 className="nx-attention-column__heading">{ATTENTION_LABELS.nexsoss}</h3>
          {model.nexsoss.length > 0 ? <Cards items={model.nexsoss} {...cardProps} /> : null}
          {model.pickedUp.length > 0 ? (
            <details className="nx-attention-column__picked-up">
              <summary>{ATTENTION_LABELS.pickedUp(model.pickedUp.length)}</summary>
              <Cards items={model.pickedUp} {...cardProps} />
            </details>
          ) : null}
        </section>
      ) : null}

      {model.info.length > 0 ? (
        <section className="nx-attention-column__group" data-group="info">
          <h3 className="nx-attention-column__heading">{ATTENTION_LABELS.info}</h3>
          <Cards items={model.info} {...cardProps} />
        </section>
      ) : null}

      {rightNow ? <RightNowList groups={rightNow} /> : null}
    </aside>
  );
}
